/* eslint-disable jsx-a11y/anchor-is-valid, no-script-url */
import React from 'react';
import PropTypes from 'prop-types';
import * as urlUtil from '@bingads-webui/url-util';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import {
  RecommendationService,
  RecommendationDownloadService,
  isRecommendationTypeEnabled,
  CHANNEL_TYPES,
  COMPETITION_RECOMMENDATION_FEATURE_NAME,
  COMPETITION_RECOMMENDATION_PREFERENCES,
  COMPETITION_DEFAULT_PAGE_SIZE,
  isReadOnly,
} from '@bingads-webui-campaign/recommendation-core';
import { getCampaignAdgroupSelectorFilterConfig } from '@bingads-webui-campaign-react/campaign-adgroup-selector-filter';
import { FilterService } from '@bingads-webui/filter-service';
import { withCcJsData } from '@bingads-webui-cc-react/js-data-rest';

const appConfig = globalContext.require('component/config/index');
const permissions = appConfig.get('Permissions');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const pageContext = globalContext.require('PageContext');
const sessionStore = globalContext.requireDefault('component/js-data-storage/session-storage');
const filterPreferences = globalContext.require('component/bingads-grid/service/filter-preferences');
const cacheService = globalContext.require('Services/CacheService');
const dataContextManager = globalContext.require('SummaryDataContextManager');
const edmCampaign = globalContext.requireDefault('component/edm-campaign/index');
const jsdata = globalContext.require('component/js-data-campaign/index');
const locationMap = globalContext.requireDefault('campaignui/feature/location-targets/view/locationMap');
const preferencesService = globalContext.requireDefault('component/preference/service/index');
const asyncScheduler = globalContext.require('module/service/shared/asyncScheduler');
const cacheManager = globalContext.requireDefault('campaignui/feature/cache-manager/index');
const { getODataErrors } = globalContext.require('component/api-errors-campaign/index');
const odata = globalContext.require('component/http/channel/odata');
const constants = globalContext.require('Constants');
const SummaryViewLazy = React.lazy(() => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/recommendation-summary-view').then(({ SummaryView }) => ({ default: SummaryView })));
const DetailsViewLazy = React.lazy(() => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/recommendation-details-view').then(({ DetailsView }) => ({ default: DetailsView })));

const readOnly = isReadOnly(appConfig);

class CompetitionRecommendationsView extends React.Component {
  static propTypes = {
    filterService: PropTypes.shape({
      getCombinedFilter: PropTypes.func,
    }),
    grid: PropTypes.shape({
      showAlertHandler: PropTypes.func.isRequired,
    }).isRequired,
    scope: PropTypes.shape({
      customerId: PropTypes.string.isRequired,
      accountId: PropTypes.string.isRequired,
      campaignId: PropTypes.string,
      adGroupId: PropTypes.string,
      token: PropTypes.string.isRequired,
      levelAt: PropTypes.string.isRequired,
    }).isRequired,
    recommendationType: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
    parentScenario: PropTypes.object,
    el: PropTypes.string.isRequired,
    guid: PropTypes.string.isRequired, // for adinsight view event log in aria
    // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
    perfMarker: PropTypes.object,
    logExpInfo: PropTypes.func.isRequired,
    viewDetails: PropTypes.func.isRequired,
    viewSummary: PropTypes.func.isRequired,
    initialToolbarConfig: PropTypes.objectOf(PropTypes.any),
    toolbar: PropTypes.element,
    ccJsData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    recommendationType: null,
    initialToolbarConfig: null,
    toolbar: null,
    filterService: null,
    ccJsData: null,
  };

  constructor(props) {
    super(props);
    const { scope } = this.props;
    this.currentScenario =
      instrumentation.loadCompetitionRecommendation.create(props.parentScenario);
    this.currentActivity = this.currentScenario.loadCompetitionRecommendation.create();

    const showAlertHandler = this.props.grid && this.props.grid.showAlertHandler;
    this.dataService = new RecommendationService({
      scope,
      showAlertHandler,
      i18n,
      currentScenario: this.currentScenario,
      currentActivity: this.currentActivity,
      odataPath: appConfig.get('adinsight.odata.url'),
      permissions,
      lcid: pageContext.CultureLcid,
      ccJsData: props.ccJsData,
      cacheManager,
    });

    this.downloadService = null;
    if (permissions.IsBAACompetitionRecommendationsDownloadEnabled === true) {
      this.downloadService = new RecommendationDownloadService({
        alertCenter: window.App.views.alertCenter,
        asyncScheduler,
        cacheManager,
        currentActivity: this.currentActivity,
        getODataErrors,
        i18n,
        odata,
        pageContext,
        permissions,
      });
    }

    this.legacyDependencies = {
      sessionStore,
      i18n,
      filterPreferences,
      cacheService,
      jsdata,
      edmCampaign,
      dataContextManager,
      locationMap,
      preferencesService,
      appConfig,
      constants,
      permissions,
    };
  }

  initializeFilter = () => {
    this.filterConfig = getCampaignAdgroupSelectorFilterConfig({
      scope: this.props.scope,
      entitySchema: {
        properties: {},
      },
      featureName: COMPETITION_RECOMMENDATION_FEATURE_NAME,
      i18n,
      sessionStore,
      preferenceStoreFactory: filterPreferences.getFilters,
      cacheService,
      dataContextManager,
      edmCampaign,
      jsdata,
    });
    this.filterService = new FilterService(this.filterConfig);
  }

  render() {
    const channel = CHANNEL_TYPES.COMPETITION;
    const showDetailsView = this.props.recommendationType
      && isRecommendationTypeEnabled(
        this.props.recommendationType,
        channel,
        permissions
      );

    const options = {
      type: showDetailsView ? this.props.recommendationType : null,
      dataService: this.dataService,
      downloadService: this.downloadService,
      currency: pageContext.AccountCurrencyCode,
      scope: this.props.scope,
      deps: this.legacyDependencies,
      channel,
      el: this.props.el,
      filterConfig: this.filterConfig,
      filterService: this.props.filterService ? this.props.filterService : this.filterService,
      currentActivity: this.currentActivity,
      readOnly,
      context: {
        Level: this.props.scope.levelAt,
        Source: null,
      },
      guid: this.props.guid,
      perfMarker: this.props.perfMarker,
      logExpInfo: this.props.logExpInfo,
    };

    if (showDetailsView && appConfig.get('recsrc') !== 'summarypage') {
      options.context.Source = urlUtil.getParameter('recsrc');
    }

    return (
      <div className="competition-recommendations">
        <React.Suspense fallback={null}>
          {showDetailsView ?
            <DetailsViewLazy
              {...options}
              viewSummary={this.props.viewSummary}
              preferencesName={COMPETITION_RECOMMENDATION_PREFERENCES}
              defaultPageSize={COMPETITION_DEFAULT_PAGE_SIZE}
              defaultPageNumber={0}
              initialToolbarConfig={this.props.initialToolbarConfig}
              toolbar={this.props.toolbar}
            /> :
            <SummaryViewLazy
              {...options}
              viewDetails={this.props.viewDetails}
              viewSummary={this.props.viewSummary}
              initialToolbarConfig={this.props.initialToolbarConfig}
              toolbar={this.props.toolbar}
            />
          }
        </React.Suspense>

      </div>
    );
  }
}

export const CompetitionRecommendations = withCcJsData(CompetitionRecommendationsView);
